import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Form } from "react-bootstrap";
import { faPaperPlane, faComments } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import {
  CalendarSvgIcon,
  FilterSvgIcon,
  GearSvgIcon,
  NoPhoneSvgIcon,
  OnOffSvgIcon,
  PhoneSvgIcon,
  PlusSvgIcon,
  ReservationSvgIcon,
  TableSvgIcon,
  UsersSvgIcon,
  XSvgIcon,
  UserSvgIcon,
  ClockSvgIcon,
  BellSvgIcon,
  BubbleTextSvgIcon,
  ZoneSvgIcon,
  PennotebookSvgIcon,
  ExtrasSvgIcon,
  ArrowUpDownIcon,
  ArrowUpDown,
  NoPhoneTwoLinesSvgIcon,
} from "../icons/SvgIcons";
import { format, parseISO } from "date-fns";

import { CustomModal } from "./CustomModal";

import { DefaultDateSmall, DefaultDateSmallMins } from "./DefaultImports";

const StyledInput = styled.input`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem; /* These values match Bootstrap's form control padding */
  &:focus {
    outline: none;
  }
`;
const ChatButton = styled.button`
  position: relative;
  border: 0;
  background: ${({ isClient }) => (isClient ? "#EE754C" : "none")};
  color: ${({ isClient }) => (isClient ? "#FFF" : "#99b0ad")};
  cursor: pointer;
  height: 40px;
  margin: 0;
  padding: ${({ isClient }) => (isClient ? "0 12px" : "0")};
  border-radius: 5px;
  font-weight: ${({ isClient }) => (isClient ? "bold" : "normal")};
  font-size: ${({ isClient }) => (isClient ? "14px" : "inherit")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-direction: row; /* sempre row, evita bug no isClient */
  box-shadow: ${({ isClient }) =>
    isClient ? "0 2px 4px rgba(0,0,0,0.2)" : "none"};
  width: ${({ isClient }) => (isClient ? "100%" : "auto")};
`;

const NotificationIndicatorOne = styled.div`
  position: absolute;
  top: 16px;
  right: 20px;
  background-color: ${({ isActive, isClient }) =>
    isActive && isClient ? "#FAF6E9" : "#FAF6E9"};
  border: ${({ isActive, isClient }) =>
    isActive ? "none" : isClient ? "1px solid #99B0AD" : "1px solid #FAF6E9"};
  width: 15px; /* Notification size */
  height: 1px;
  border-radius: 50%;
  animation: ${({ isActive }) => (isActive ? "pulse 1.5s infinite" : "none")};

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const NotificationIndicatorTwo = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: ${({ isActive, isClient }) =>
    isActive && isClient ? "#FAF6E9" : "#FAF6E9"};
  border: ${({ isActive, isClient }) =>
    isActive ? "none" : isClient ? "1px solid #99B0AD" : "1px solid #FAF6E9"};
  width: 15px; /* Notification size */
  height: 1px;
  border-radius: 50%;
  animation: ${({ isActive }) => (isActive ? "pulse 1.5s infinite" : "none")};

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const NotificationIndicatorThree = styled.div`
  position: absolute;
  top: 24px;
  right: 20px;
  background-color: ${({ isActive, isClient }) =>
    isActive && isClient ? "#FAF6E9" : "#FAF6E9"};
  border: ${({ isActive, isClient }) =>
    isActive ? "none" : isClient ? "1px solid #99B0AD" : "1px solid #FAF6E9"};
  width: 15px; /* Notification size */
  height: 1px;
  border-radius: 50%;
  animation: ${({ isActive }) => (isActive ? "pulse 1.5s infinite" : "none")};

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

function ReservationChat({
  reservation,
  isClient = false,
  setChatModalTrueOrFalse = false,
  openModal = false,
  hideButton = false,
}) {
  const [showModal, setShowModal] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const socket = useRef(null);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const chatEndRef = useRef(null);

  const restaurant_info = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurant_info;

  useEffect(() => {
    if (openModal === true) {
      setShowModal(openModal);
    }
  }, [openModal]);

  useEffect(() => {
    if (showModal) {
      let sender = userInfo ? userInfo.me : reservation.full_name;
      const websocketBaseURL = process.env.REACT_APP_WEBSOCKET_URL;
      socket.current = new WebSocket(
        `${websocketBaseURL}chat/init/${reservation.token}/${sender}/${
          isClient ? "client" : "restaurant"
        }/`
      );

      socket.current.onmessage = (event) => {
        const data = JSON.parse(event.data);

        // Check for chat history type message
        if (data.type === "chat_history") {
          // Sort the messages by timestamp in ascending order
          const sortedMessages = data.messages.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
          });
          setChatHistory(sortedMessages);
        } else {
          // For normal chat messages, append to the current chat history
          // and ensure the chatHistory is sorted after appending the new message
          setChatHistory((prevHistory) => {
            const updatedHistory = [...prevHistory, data];
            return updatedHistory.sort((a, b) => {
              const dateA = new Date(a.timestamp);
              const dateB = new Date(b.timestamp);
              return dateA - dateB;
            });
          });
        }
        console.log("data.messages: ", data.messages);
      };

      socket.current.onopen = () => {
        console.log("WebSocket Connected");
      };

      socket.current.onclose = () => {
        console.log("WebSocket Disconnected");
      };

      return () => socket.current?.close();
    }
  }, [userInfo, showModal]);

  const sendMessage = () => {
    if (socket.current && newMessage.trim()) {
      socket.current.send(
        JSON.stringify({
          message: newMessage,
          sender: userInfo ? userInfo.name : reservation.full_name,
          client_or_restaurant: isClient ? "client" : "restaurant",
        })
      );
    }
    setNewMessage("");
    console.log("isClient", isClient, isClient ? "client" : "restaurant");
  };

  const isUserMessage = (senderName) => {
    if (userInfo && senderName === userInfo.name) {
      return true;
    } else if (!userInfo && senderName === reservation.full_name) {
      return true;
    }
    return false;
  };

  const closeModal = () => {
    if (setChatModalTrueOrFalse) {
      setChatModalTrueOrFalse(false);
    }
    setShowModal(false);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView();
    console.log(
      "reservation",
      reservation && reservation.chat_notification_restaurant
    );
    console.log("userInfo: ", userInfo && userInfo.name);
  }, [chatHistory, reservation, showModal]);

  return (
    <>
      {!hideButton && (
        <>
          <ChatButton isClient={isClient} onClick={() => setShowModal(true)}>
            {isClient && "Mensagens"}
            <BubbleTextSvgIcon
              width={isClient ? 30 : 43}
              height={isClient ? 30 : 43}
              strokeColor={isClient ? "#FAF6E9" : "#FAF6E9"}
            />

            {/* <-- aqui: só renderiza os traços quando NÃO for client */}
            {!isClient && reservation.chat_has_messages && (
              <>
                <NotificationIndicatorOne
                  isActive={
                    (reservation.chat_notification_restaurant &&
                      Array.isArray(reservation.chat_notification_restaurant) &&
                      reservation.chat_notification_restaurant.includes(
                        userInfo && userInfo.name
                      )) ||
                    (reservation.chat_notification_client && isClient)
                  }
                  isClient={isClient}
                />
                <NotificationIndicatorTwo
                  isActive={
                    (reservation.chat_notification_restaurant &&
                      Array.isArray(reservation.chat_notification_restaurant) &&
                      reservation.chat_notification_restaurant.includes(
                        userInfo && userInfo.name
                      )) ||
                    (reservation.chat_notification_client && isClient)
                  }
                  isClient={isClient}
                />
                <NotificationIndicatorThree
                  isActive={
                    (reservation.chat_notification_restaurant &&
                      Array.isArray(reservation.chat_notification_restaurant) &&
                      reservation.chat_notification_restaurant.includes(
                        userInfo && userInfo.name
                      )) ||
                    (reservation.chat_notification_client && isClient)
                  }
                  isClient={isClient}
                />
              </>
            )}
          </ChatButton>
        </>
      )}
      <ChatModal
        show={showModal}
        onHide={closeModal}
        centered
        title={
          <TitleWrapper>
            <IconGroup>
              <UserSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              <Truncate>{reservation.full_name}</Truncate>
            </IconGroup>

            <IconGroup>
              <CalendarSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              <DefaultDateSmall
                date={moment.tz(
                  `${reservation.date}`,
                  "YYYY-MM-DD",
                  restaurants.timezone
                )}
              />
            </IconGroup>

            <IconGroup>
              <ClockSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              {reservation.time}
            </IconGroup>

            <IconGroup>
              <UsersSvgIcon width={24} height={24} strokeColor="#FAF6E9" />
              {reservation.number_of_people}
            </IconGroup>

            <IconGroup>
              <ZoneSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              {reservation.table_place_preference
                ? reservation.table_place_preference.place_of_table
                : "Qualquer Sala"}
            </IconGroup>
          </TitleWrapper>
        }
        footer={
          <InputContainer>
            <StyledTextarea
              placeholder="Escrever mensagem…"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <SendButton onClick={sendMessage}>
              <FontAwesomeIcon icon={faPaperPlane} size="lg" color="#9FBBC1" />
            </SendButton>
          </InputContainer>
        }
      >
        {/* children — conteúdo do corpo */}
        <ChatHistoryContainer>
          {chatHistory.map((msg, idx) => (
            <React.Fragment key={idx}>
              <SenderName isUserMessage={isUserMessage(msg.sender)}>
                <span className="sender">{msg.sender}</span>
              </SenderName>

              <TimestampCenter>
                <span>
                  {msg.timestamp ? (
                    <DefaultDateSmallMins date={msg.timestamp} />
                  ) : (
                    "hoje"
                  )}
                </span>
              </TimestampCenter>

              <MessageBubble
                className={
                  isUserMessage(msg.sender) ? "user-message" : "other-message"
                }
              >
                {msg.message.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </MessageBubble>
            </React.Fragment>
          ))}
          <div ref={chatEndRef} />
        </ChatHistoryContainer>
      </ChatModal>
    </>
  );
}

export default ReservationChat;

const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.2;
  color: #faf6e9;
  margin-right: 36px;           

  @media (max-width: 576px) {
    font-size: 11px;            
    gap: 4px;                   
    margin-right: 28px;        
`;

const IconGroup = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    @media (max-width: 576px) {
      width: 16px !important;
      height: 16px !important;
    }
  }
`;

const Truncate = styled.span`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 576px) {
    max-width: 70px;
  }
`;

const ChatModal = styled(CustomModal)`
  .modal-dialog {
    max-width: 500px;
    width: 90%;
    min-height: unset;
  }

  .modal-content {
    height: 700px; /* altura desktop */
    display: flex;
    flex-direction: column;
  }
  .modal-header {
    @media (max-width: 576px) {
      padding: 6px 12px; /* mobile: mais baixo e estreito */
    }
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
  }

  .modal-footer {
    display: block;
    padding: 0;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      width: 95%;
      margin: 0 auto;
    }

    .modal-content {
      height: 80vh;
    }
  }
`;

/* HISTÓRICO */
const ChatHistoryContainer = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding: 10px;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;

/* BOLHA DE MENSAGEM */
const MessageBubble = styled.div`
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  color: #fff;
  word-break: break-word;

  &.user-message {
    background-color: #ee754c; /* laranja + escuro */
    margin-left: auto;
    border-bottom-right-radius: 0;
  }
  &.other-message {
    background-color: #99b0ad; /* laranja + claro */
    margin-right: auto;
    border-bottom-left-radius: 0;
  }
`;

/* NOME E TIMESTAMP */
const SenderName = styled.div`
  margin-bottom: 5px;
  text-align: ${({ isUserMessage }) => (isUserMessage ? "right" : "left")};
  color: #6c757d;
  font-size: 0.9rem;
`;

const TimestampCenter = styled.div`
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 5px;
  color: #6c757d;
`;

/* INPUT + BOTÃO ENVIAR */
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-top: 1px solid #ced4da;
`;

const StyledTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  resize: none;
  &:focus {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 10px;
  }
`;

const SendButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 15px;
`;
